/**
 * Client-side operational-log forwarder.
 *
 * SPA-side errors + gateway-state events were a blind spot for
 * platform admins: things that broke in the user's browser (gateway
 * disconnects, agent failures, unhandled JS errors, fetch failures)
 * never reached the centralized `operationalLogs` collection unless
 * Sentry happened to capture them. The user only saw a banner; ops
 * had to dig through screenshots / console captures to debug.
 *
 * This module mirrors the wire shape of `lx-obs`'s LogRecord so the
 * records land in the same `operationalLogs` collection alongside
 * lexserver + lexruntime emissions, queryable from the LexPortal
 * Operational Logs viewer with a `forwarded_from_service: "lexide-client"`
 * filter.
 *
 * What it captures:
 *   - `clientLog.error/warn/info(...)`: explicit emission from anywhere
 *     in the SPA (gateway state-change, fetch failure, error boundary).
 *   - `window.error` (uncaught JS errors): wired by `installGlobalCapture`.
 *   - `unhandledrejection` (unhandled Promise rejections): same.
 *
 * Sending:
 *   - Records are batched in a process-local FIFO buffer. Flush on:
 *     (a) buffer reaching 25 entries, (b) `FLUSH_INTERVAL_MS` (5s)
 *     elapsed since last flush, (c) `pagehide` / `beforeunload` (uses
 *     `navigator.sendBeacon` for the last gasp).
 *   - POST to `/api/client-log` with the user's Lexiun JWT (Bearer
 *     header). Server stamps user_id/org_id from the JWT, so the SPA
 *     doesn't need to include them in `fields`.
 *   - On HTTP error or no-JWT: drop silently. The client-log channel
 *     must NEVER throw or block the SPA's main flow.
 */

import { getToken } from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";

type LogLevel = "trace" | "debug" | "info" | "warn" | "error";

interface ClientLogRecord {
  type: "log";
  level: LogLevel;
  message: string;
  timestamp_ms: number;
  subsystem?: string;
  fields?: Record<string, unknown>;
  error?: { name: string; message: string; stack?: string };
  source?: "author" | "sdk";
}

const SERVICE_SLUG = "lexide-client";
const ENDPOINT = "/api/client-log";
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 25;
const MAX_BUFFER = 200; // Hard cap so a flush-storm doesn't OOM the tab.

const buffer: ClientLogRecord[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush(opts?: { useBeacon?: boolean }): Promise<void> {
  if (buffer.length === 0) return;
  const records = buffer.splice(0, buffer.length);
  // Best-effort. NEVER throw out of this function — caller paths
  // include `unload`-time `pagehide` listeners where exceptions are
  // dropped silently anyway.
  try {
    if (opts?.useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      // sendBeacon doesn't accept Authorization headers. We embed the
      // JWT in the body as a fallback; server falls back to header-only
      // if the body shape doesn't match — this path is a last-gasp on
      // page unload, so a few dropped records is acceptable. We use a
      // Blob to set Content-Type explicitly.
      const token = await getToken().catch(() => null);
      if (!token) return;
      const blob = new Blob(
        [JSON.stringify({ records })],
        { type: "application/json" },
      );
      navigator.sendBeacon(`${ENDPOINT}?service=${SERVICE_SLUG}&t=${encodeURIComponent(token)}`, blob);
      return;
    }
    const token = await getToken().catch(() => null);
    if (!token) return;
    await fetch(`${ENDPOINT}?service=${SERVICE_SLUG}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Lex-Client-Service": SERVICE_SLUG,
        // Carry a fresh trace so the inbound /api/client-log handler
        // shares a trace_id with the originating user action (see
        // lexserver/src/middleware/sentry-scope.ts).
        traceparent: newTraceparent(),
      },
      body: JSON.stringify({ records }),
      keepalive: true, // survive a page-leaving fetch
    });
  } catch {
    // Drop silently — the client-log channel must never break the SPA.
  }
}

function emit(level: LogLevel, message: string, opts?: {
  subsystem?: string;
  fields?: Record<string, unknown>;
  error?: Error;
}): void {
  if (buffer.length >= MAX_BUFFER) {
    // Drop oldest to keep buffer bounded.
    buffer.splice(0, FLUSH_BATCH_SIZE);
  }
  const record: ClientLogRecord = {
    type: "log",
    level,
    message,
    timestamp_ms: Date.now(),
    subsystem: opts?.subsystem,
    fields: opts?.fields,
    source: "author",
  };
  if (opts?.error) {
    record.error = {
      name: opts.error.name,
      message: opts.error.message,
      stack: opts.error.stack,
    };
  }
  buffer.push(record);
  if (buffer.length >= FLUSH_BATCH_SIZE) {
    void flush();
  } else {
    scheduleFlush();
  }
}

export const clientLog = {
  trace: (message: string, fields?: Record<string, unknown>) =>
    emit("trace", message, { fields }),
  debug: (message: string, fields?: Record<string, unknown>) =>
    emit("debug", message, { fields }),
  info: (message: string, fields?: Record<string, unknown>) =>
    emit("info", message, { fields }),
  warn: (message: string, fields?: Record<string, unknown>) =>
    emit("warn", message, { fields }),
  error: (message: string, fieldsOrError?: Record<string, unknown> | Error, error?: Error) => {
    if (fieldsOrError instanceof Error) {
      emit("error", message, { error: fieldsOrError });
    } else {
      emit("error", message, { fields: fieldsOrError, error });
    }
  },
  /** Tag a `subsystem` for a logical sub-component (chat, gateway, fetch). */
  forSubsystem(subsystem: string) {
    return {
      trace: (m: string, f?: Record<string, unknown>) => emit("trace", m, { subsystem, fields: f }),
      debug: (m: string, f?: Record<string, unknown>) => emit("debug", m, { subsystem, fields: f }),
      info: (m: string, f?: Record<string, unknown>) => emit("info", m, { subsystem, fields: f }),
      warn: (m: string, f?: Record<string, unknown>) => emit("warn", m, { subsystem, fields: f }),
      error: (m: string, fOrE?: Record<string, unknown> | Error, e?: Error) => {
        if (fOrE instanceof Error) emit("error", m, { subsystem, error: fOrE });
        else emit("error", m, { subsystem, fields: fOrE, error: e });
      },
    };
  },
  flush: () => flush(),
};

/**
 * Install global window listeners that forward uncaught errors +
 * Promise rejections to the client-log channel. Call once at app
 * bootstrap (before any other module subscribes).
 */
export function installGlobalCapture(): void {
  if (installed) return;
  installed = true;

  if (typeof window !== "undefined") {
    window.addEventListener("error", (ev) => {
      emit("error", `window.error: ${ev.message}`, {
        subsystem: "global",
        error: ev.error instanceof Error ? ev.error : undefined,
        fields: {
          filename: ev.filename,
          lineno: ev.lineno,
          colno: ev.colno,
        },
      });
    });
    window.addEventListener("unhandledrejection", (ev) => {
      const reason = ev.reason;
      const errObj =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === "string" ? reason : JSON.stringify(reason).slice(0, 500));
      emit("error", `unhandledrejection: ${errObj.message}`, {
        subsystem: "global",
        error: errObj,
      });
    });
    // Last-gasp flush on page unload — sendBeacon survives a
    // navigation that a regular fetch wouldn't.
    window.addEventListener("pagehide", () => {
      void flush({ useBeacon: true });
    });
    // beforeunload is fired in more browsers but on fewer events;
    // belt-and-suspenders.
    window.addEventListener("beforeunload", () => {
      void flush({ useBeacon: true });
    });
  }
}
